import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "@/context/NotificationContext";
import { dataApi } from "@/api";
import { extractErrorMessage } from "@/api/axios";
import {
  DataFormat,
  ImportMode,
  DATA_FORMAT_FILE_EXTENSIONS,
  MEMBERSHIP_EXPORT_FILENAME_PREFIX,
} from "@/constants";

export function useSettingsDataExchange() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [exportFormat, setExportFormat] = useState<DataFormat>(DataFormat.Json);
  const [importFormat, setImportFormat] = useState<DataFormat>(DataFormat.Json);
  const [importMode, setImportMode] = useState<ImportMode>(ImportMode.Merge);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await dataApi.exportAll(exportFormat);
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, 19);
      const filename = `${MEMBERSHIP_EXPORT_FILENAME_PREFIX}${timestamp}.${exportFormat}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify(t("settings.exportSuccess"), "success");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setExporting(false);
    }
  }, [exportFormat, notify, t]);

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;
    setImporting(true);
    try {
      const result = await dataApi.importAll(
        selectedFile,
        importFormat,
        importMode,
      );
      const summary = Object.entries(result.imported)
        .filter(([, count]) => count > 0)
        .map(([key, count]) => `${key}: ${count}`)
        .join(", ");
      notify(
        t("settings.importSuccess", {
          summary: summary || t("settings.importSummaryEmpty"),
        }),
        "success",
      );
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setImporting(false);
    }
  }, [selectedFile, importFormat, importMode, notify, t]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setSelectedFile(file);
      if (file) {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === DataFormat.Json) setImportFormat(DataFormat.Json);
        else if (ext === "yaml" || ext === "yml")
          setImportFormat(DataFormat.Yaml);
        else if (ext === DataFormat.Csv) setImportFormat(DataFormat.Csv);
      }
    },
    [],
  );

  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  return {
    fileInputRef,
    exportFormat,
    setExportFormat,
    importFormat,
    setImportFormat,
    importMode,
    setImportMode,
    selectedFile,
    exporting,
    importing,
    handleExport,
    handleImport,
    handleFileSelect,
    clearSelectedFile,
    acceptExtensions: DATA_FORMAT_FILE_EXTENSIONS[importFormat].join(","),
  };
}
